"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { format, subDays, subYears } from "date-fns";
import { zhCN } from "date-fns/locale";
import { CalendarDays, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";

interface DatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  fromDate?: Date;
  toDate?: Date;
  disabled?: boolean;
  variant?: "default" | "scene";
}

export function DatePicker({
  date,
  onDateChange,
  onSelect,
  placeholder = "选择日期",
  fromDate,
  toDate,
  disabled,
  variant = "default",
}: DatePickerProps) {
  // 支持两种属性名，优先使用 onSelect
  const handleSelect = onSelect || onDateChange;
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({
    position: "fixed",
    top: 0,
    left: 0,
    width: 360,
    maxHeight: 520,
  });
  const [compactPanel, setCompactPanel] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const displayText = date ? format(date, "yyyy年M月d日", { locale: zhCN }) : placeholder;
  const sceneVariant = variant === "scene";
  const latestDate = toDate || new Date();
  const earliestDate = fromDate || new Date(2019, 0, 1);
  const calendarClassNames = compactPanel
    ? {
        month: "flex w-full flex-col gap-2",
        months: "relative flex flex-col gap-2 md:flex-row",
        week: "mt-1 flex w-full",
        dropdowns: "flex h-(--cell-size) w-full items-center justify-center gap-1 text-xs font-semibold sm:text-sm",
      }
    : {
        dropdowns: "flex h-(--cell-size) w-full items-center justify-center gap-1 text-xs font-semibold sm:text-sm",
      };

  const quickDates = [
    { label: "今天", date: latestDate },
    { label: "昨天", date: subDays(latestDate, 1) },
    { label: "7天前", date: subDays(latestDate, 7) },
    { label: "去年今日", date: subYears(latestDate, 1) },
  ].filter((item) => !fromDate || item.date >= fromDate);

  const selectDate = (nextDate: Date | undefined) => {
    setOpen(false);
    handleSelect?.(nextDate);
  };

  const updatePanelPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") return;

    const rect = trigger.getBoundingClientRect();
    const viewportPadding = window.innerWidth < 640 ? 10 : 16;
    const panelGap = 8;
    const panelWidth = Math.min(360, window.innerWidth - viewportPadding * 2);
    const availableBelow = window.innerHeight - rect.bottom - panelGap - viewportPadding;
    const availableAbove = rect.top - panelGap - viewportPadding;
    const preferredHeight = 520;
    const openBelow = availableBelow >= Math.min(preferredHeight, availableAbove);
    const availableHeight = Math.max(
      320,
      Math.min(preferredHeight, openBelow ? availableBelow : availableAbove)
    );
    const left = Math.min(
      Math.max(viewportPadding, rect.right - panelWidth),
      window.innerWidth - panelWidth - viewportPadding
    );
    const top = openBelow
      ? Math.min(rect.bottom + panelGap, window.innerHeight - availableHeight - viewportPadding)
      : Math.max(viewportPadding, rect.top - availableHeight - panelGap);

    setCompactPanel(availableHeight < 470 || window.innerWidth < 390);
    setPanelStyle({
      position: "fixed",
      top,
      left,
      width: panelWidth,
      maxHeight: availableHeight,
    });
  }, []);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!open) return;

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);

    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const panel = (
    <div
      ref={panelRef}
      data-date-picker-panel
      className="z-[80] flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-popover p-0 text-popover-foreground shadow-xl shadow-slate-900/10 outline-none dark:border-slate-800"
      style={panelStyle}
      role="dialog"
      aria-label="选择历史日期"
    >
      <div className={cn("shrink-0 border-b px-4", compactPanel ? "py-2.5" : "py-3")}>
        <div className="text-sm font-semibold text-foreground">选择历史日期</div>
        <div className="mt-1 text-xs text-muted-foreground">
          {fromDate && latestDate
            ? `${format(fromDate, "yyyy/MM/dd")} - ${format(latestDate, "yyyy/MM/dd")}`
            : "选择一个历史热搜快照"}
        </div>
      </div>

      <div className={cn("grid shrink-0 grid-cols-2 gap-2 px-4 sm:grid-cols-4", compactPanel ? "pt-2" : "pt-3")}>
        {quickDates.map((item) => (
          <Button
            key={item.label}
            type="button"
            variant="outline"
            size="sm"
            className={cn("rounded-md px-2 text-xs", compactPanel ? "h-8" : "h-9")}
            disabled={disabled}
            onClick={() => selectDate(item.date)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      <div className={cn("min-h-0 flex-1 overflow-hidden px-3", compactPanel ? "pb-2 pt-1.5" : "pb-3 pt-2")}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={selectDate}
          fromDate={fromDate}
          toDate={toDate}
          startMonth={earliestDate}
          endMonth={latestDate}
          captionLayout="dropdown"
          initialFocus
          locale={zhCN}
          buttonVariant="ghost"
          className={cn(
            "mx-auto w-full",
            compactPanel
              ? "p-1 [--cell-size:clamp(1.85rem,7vw,2.2rem)]"
              : "[--cell-size:clamp(2rem,7.5vw,2.45rem)]"
          )}
          classNames={calendarClassNames}
        />
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="选择热搜历史日期"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "group inline-flex h-8 min-w-[112px] items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2 text-left text-xs shadow-sm ring-offset-background transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/10 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-900 sm:h-11 sm:min-w-[172px] sm:gap-3 sm:rounded-xl sm:px-3 sm:text-sm sm:hover:-translate-y-px sm:hover:shadow-md",
          sceneVariant && "h-7 min-w-[92px] border-transparent bg-transparent px-2 text-white shadow-none ring-offset-transparent hover:border-transparent hover:bg-white/10 focus-visible:ring-white/30 dark:border-transparent dark:bg-transparent dark:hover:bg-white/10 sm:h-8 sm:min-w-[116px] sm:gap-2 sm:rounded-md sm:px-2.5 sm:text-xs sm:hover:translate-y-0 sm:hover:shadow-none",
          !date && !sceneVariant && "text-muted-foreground"
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5 sm:gap-2">
          <span
            className={cn(
              "hidden h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-950 sm:flex",
              sceneVariant && "h-5 w-5 rounded-md bg-white/10 text-white shadow-none dark:bg-white/10 dark:text-white"
            )}
          >
            <CalendarDays className={cn("h-4 w-4", sceneVariant && "h-3.5 w-3.5")} />
          </span>
          <span className="min-w-0">
            <span className={cn("hidden text-[10px] font-medium leading-3 text-muted-foreground sm:block", sceneVariant && "sr-only")}>
              历史日期
            </span>
            <span
              className={cn(
                "block truncate font-semibold leading-5 text-foreground",
                !date && "font-medium text-muted-foreground",
                sceneVariant && "text-white",
                sceneVariant && !date && "text-white/90"
              )}
            >
              {displayText}
            </span>
          </span>
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform sm:h-4 sm:w-4", sceneVariant && "text-white/70", open && "rotate-180")} />
      </button>
      {mounted && open ? createPortal(panel, document.body) : null}
    </>
  );
}
